import { UploadStatus } from 'store/organisation/models';
import i18n from 'i18next';

export type UploadFileIndicatorState =
  | 'pending'
  | 'active'
  | 'complete'
  | 'error';

const getUploadStatusFullLabel = (status: UploadStatus): string => {
  switch (status) {
    case UploadStatus.WAITING_FOR_AI:
      return i18n.t('upload-status.waiting-for-ai');
    case UploadStatus.BUILDING_TRANSACTIONS:
      return i18n.t('upload-status.building-transactions');
    case UploadStatus.ACTIVE:
      return i18n.t('common.done');
    case UploadStatus.AI_ERROR:
      return i18n.t('upload-status.ai-error');
    case UploadStatus.UNKNOWN_ERROR:
      return i18n.t('common.error');
    case UploadStatus.INVALID_FILE:
      return i18n.t('upload-status.invalid-file');
    case UploadStatus.AI_INVALID_FILE:
      return i18n.t('upload-status.ai-invalid-file');
    case UploadStatus.MULTI_DOCUMENTS_FILE:
      return i18n.t('upload-status.multi-documents-file');
    case UploadStatus.TOO_MANY_DOCUMENTS:
      return i18n.t('upload-status.too-many-documents');
    case UploadStatus.UNSUPPORTED_CURRENCY:
      return i18n.t('upload-status.unsupported-currency');
    case UploadStatus.DUPLICATED_DOCUMENT:
      return i18n.t('upload-status.duplicated-document');
    case UploadStatus.EMPTY_DATE:
      return i18n.t('upload-status.empty-date');
    default:
      return formatRawUploadStatus(status);
  }
};

const getUploadStatusShortLabelForStatus = (status: UploadStatus): string => {
  switch (status) {
    case UploadStatus.WAITING_FOR_AI:
      return i18n.t('upload-status.processing-short');
    case UploadStatus.BUILDING_TRANSACTIONS:
      return i18n.t('upload-status.building-short');
    case UploadStatus.ACTIVE:
      return i18n.t('common.done');
    case UploadStatus.AI_ERROR:
    case UploadStatus.UNKNOWN_ERROR:
      return i18n.t('common.error');
    case UploadStatus.INVALID_FILE:
    case UploadStatus.AI_INVALID_FILE:
      return i18n.t('upload-status.invalid-short');
    case UploadStatus.MULTI_DOCUMENTS_FILE:
      return i18n.t('upload-status.analyzing-short');
    case UploadStatus.TOO_MANY_DOCUMENTS:
      return i18n.t('upload-status.limit-short');
    case UploadStatus.UNSUPPORTED_CURRENCY:
      return i18n.t('common.currency');
    case UploadStatus.DUPLICATED_DOCUMENT:
      return i18n.t('upload-status.duplicated-short');
    case UploadStatus.EMPTY_DATE:
      return i18n.t('upload-status.empty-date-short');
    default:
      return formatRawUploadStatus(status);
  }
};

/** Human-readable fallback when status is not in {@link UploadStatus}. */
export const formatRawUploadStatus = (status: unknown): string => {
  const raw = String(status ?? '').trim();
  if (!raw) return i18n.t('common.unknown');

  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      if (lower === 'ai') return 'AI';
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

export const isUploadStatusError = (status: unknown): boolean => {
  const parsed = parseUploadStatus(status);
  if (!parsed) return false;
  return (
    parsed === UploadStatus.AI_ERROR ||
    parsed === UploadStatus.UNKNOWN_ERROR ||
    parsed === UploadStatus.INVALID_FILE ||
    parsed === UploadStatus.AI_INVALID_FILE ||
    parsed === UploadStatus.TOO_MANY_DOCUMENTS ||
    parsed === UploadStatus.UNSUPPORTED_CURRENCY ||
    parsed === UploadStatus.DUPLICATED_DOCUMENT ||
    parsed === UploadStatus.EMPTY_DATE
  );
};

export const isUploadStatusProcessing = (status: unknown): boolean => {
  const parsed = parseUploadStatus(status);
  return (
    parsed === UploadStatus.WAITING_FOR_AI ||
    parsed === UploadStatus.BUILDING_TRANSACTIONS
  );
};

export const isDeletedReceiptStatus = (status: unknown): boolean =>
  String(status).toUpperCase() === 'DELETED';

export const parseUploadStatus = (status: unknown): UploadStatus | null => {
  const normalized = String(status ?? '').trim().toUpperCase();
  if (Object.values(UploadStatus).includes(normalized as UploadStatus)) {
    return normalized as UploadStatus;
  }
  return null;
};

export const getUploadStatusMessage = (
  status: unknown,
  errorReason?: string | null
): string => {
  const uploadStatus = parseUploadStatus(status);
  const trimmedError = errorReason?.trim();

  if (!uploadStatus) {
    return formatRawUploadStatus(status);
  }

  if (isUploadStatusError(uploadStatus) && trimmedError) {
    return trimmedError;
  }

  return getUploadStatusFullLabel(uploadStatus);
};

export const getUploadFileIndicatorState = (
  status: unknown
): UploadFileIndicatorState => {
  const parsed = parseUploadStatus(status);
  if (!parsed) return 'active';
  if (parsed === UploadStatus.ACTIVE) return 'complete';
  if (isUploadStatusError(parsed)) return 'error';
  return 'active';
};

/** Friendly label for upload progress (or errorReason for errors). */
export const getProgressFromUploadStatus = (
  status: unknown,
  errorReason?: string
): {
  label: string;
  indicatorState: UploadFileIndicatorState;
} => ({
  label: getUploadStatusMessage(status, errorReason),
  indicatorState: getUploadFileIndicatorState(status),
});

export const getUploadStatusShortLabel = (status: unknown): string => {
  const normalized = String(status ?? '').toUpperCase();
  if (normalized === 'DELETED') return i18n.t('upload-status.deleted');

  const uploadStatus = parseUploadStatus(status);
  if (uploadStatus) {
    return getUploadStatusShortLabelForStatus(uploadStatus);
  }

  return formatRawUploadStatus(status);
};

export const getUploadStatusTooltip = (
  status: unknown,
  errorReason?: string | null
): string | undefined => {
  const uploadStatus = parseUploadStatus(status);
  if (!uploadStatus) {
    return errorReason?.trim() || undefined;
  }

  const message = getUploadStatusMessage(uploadStatus, errorReason);
  const short = getUploadStatusShortLabelForStatus(uploadStatus);
  if (message && message !== short) return message;

  return undefined;
};

/** Matches MessageBox notification variants. */
export type UploadStatusTagVariant =
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'default';

/** Matches ColoredTag / StatusGetter styling used across the app. */
export const UPLOAD_STATUS_TAG_STYLES: Record<
  UploadStatusTagVariant,
  { color: string; border: string; background: string }
> = {
  success: {
    color: 'var(--color-status-success, #058181)',
    border: 'var(--color-border, #dbeae3)',
    background: 'var(--color-status-success-bg, #E9F6F7)',
  },
  error: { color: '#D13131', border: '#D13131', background: '#FCE7E7' },
  info: { color: '#C38A20', border: '#FFBC3E', background: '#FFF5E2' },
  warning: { color: '#C38A20', border: '#FFBC3E', background: '#FFF5E2' },
  default: {
    color: 'var(--color-text-muted, #5a6a6a)',
    border: '#D7D7D7',
    background: 'var(--color-surface, #F3F7F8)',
  },
};

export const getUploadStatusTagVariant = (
  status: unknown
): UploadStatusTagVariant => {
  const uploadStatus = parseUploadStatus(status);
  if (uploadStatus) {
    if (uploadStatus === UploadStatus.ACTIVE) return 'success';
    if (isUploadStatusError(uploadStatus)) return 'error';
    if (
      uploadStatus === UploadStatus.WAITING_FOR_AI ||
      uploadStatus === UploadStatus.BUILDING_TRANSACTIONS ||
      uploadStatus === UploadStatus.MULTI_DOCUMENTS_FILE
    ) {
      return 'info';
    }
    return 'default';
  }

  const normalized = String(status ?? '').toLowerCase();
  if (!normalized) return 'default';
  if (
    normalized.includes('error') ||
    normalized.includes('fail') ||
    normalized.includes('invalid')
  ) {
    return 'error';
  }
  if (
    normalized.includes('success') ||
    normalized.includes('done') ||
    normalized.includes('active')
  ) {
    return 'success';
  }
  if (
    normalized.includes('processing') ||
    normalized.includes('upload') ||
    normalized.includes('wait')
  ) {
    return 'info';
  }
  return 'default';
};
